import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { FirestoreUser, IFirestoreInviteUser, NetworkConstants, UserRoles, isArray } from 'diwimamodelweb/lib';
import { GenericDialogComponent } from '../generic-dialog/generic-dialog.component';
import { UserForm } from '../model/form';
import { PersonService } from '../person.service';
import { UserFeedbackService } from '../user-feedback.service';
import { MatDialog } from '@angular/material/dialog';
import { SettingsService } from '../settings.service';
import { UserService } from '../user.service';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Observable } from 'rxjs';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';


const buildRoles = (_roles: any): string[] => {
  let roles: string[] = []
  if (_roles.roleAnsteller) roles.push(UserRoles.ANSTELLER)
  if (_roles.roleJagdleiter) roles.push(UserRoles.JAGDLEITER)
  if (_roles.roleUser) roles.push(UserRoles.USER)
  if (_roles.roleHunter) roles.push(UserRoles.HUNTER)
  if (_roles.roleRevierLeiter) roles.push(UserRoles.REVIERLEITER)
  if (_roles.roleRecorder) roles.push(UserRoles.RECORDER)
  return roles
}

const buildUser = (oldUserData: FirestoreUser|null, formValue: any): [FirestoreUser|null, FirestoreUser] => {
  const oldUser: any = Object.assign({}, oldUserData ? oldUserData : {})
    const {
      displayName,roleAnsteller, 
        roleJagdleiter, roleUser, roleHunter, roleRevierLeiter, roleRecorder,
    } = formValue
    const roles = buildRoles({
      roleAnsteller, 
      roleRevierLeiter, 
      roleJagdleiter, 
      roleUser,
      roleHunter,
      roleRecorder
    })
    const newUser: any = Object.assign({}, oldUserData ? oldUserData : {})
    // newUser.email = email

    newUser.displayName = displayName
    newUser.roles = roles
    return [oldUser, newUser]
}

@Component({
  selector: 'app-invite-user-form',
  templateUrl: './invite-user-form.component.html',
  styleUrls: ['./invite-user-form.component.css']
})
export class InviteUserFormComponent implements OnInit {

  @Input() data: UserForm
  @Input() formBuilder: FormBuilder
  @Input() userFeedbackService: UserFeedbackService
  @Input() objectService: PersonService
  @Input() translateService: TranslateService

  userForm: FormGroup
  user$: Observable<any> = new Observable()
  userRoles = []
  customRoles = false

  rolesSelectCtrl = new FormControl<string[]>([], { nonNullable: true })
  private rolesSelectSub?: Subscription
  roleOptions: { identifier: string; title: string }[] = []

  isSuperContext = false
  /** Current context + every sub-context, all as equal, checkable rows — no separate "Hauptkontext" field. */
  contextRows: { id: string; name: string; selected: boolean }[] = []
  /** Selection order, tracked separately since checkbox state alone doesn't carry it — the first entry becomes the primary (Hauptkontext) context on submit. */
  private contextSelectionOrder: string[] = []
  loadingAdditionalContexts = false

  constructor(public dialog: MatDialog,
    public settingsService: SettingsService,
    private userService: UserService,
    private functions: AngularFireFunctions,
    private userFeedBackService: UserFeedbackService,
    private firestore: Firestore,
  ) { }

  ngOnInit(): void {
    this.userService.user$.subscribe(value=> {
        this.user$ = value?.displayName
        this.userFeedBackService.loading.emit(false)
    })
    
    this.data.onNavBack = this.onCancle
    this.userForm = this.formBuilder.group({
      email: [, [Validators.required, Validators.email]],
      language: [, Validators.required],

      roleAnsteller: [false],
      roleJagdleiter: [false],
      roleUser: [true],
      roleHunter: [false],
      roleRevierLeiter: [false],
      roleRecorder: [false]
    })

    // NAR-5647
    const context = this.settingsService.selectedUserContext$?.value
    this.customRoles = context.modulesMap?.get('customRoles') ? true : false

    // if (this.customRoles) {
      this.userRoles = context.settingsMap.get(NetworkConstants.SETTING_USER_ROLES)?.values ? (isArray(context.settingsMap.get(NetworkConstants.SETTING_USER_ROLES)?.values) ? context.settingsMap.get(NetworkConstants.SETTING_USER_ROLES)?.values : []) :  []

      this.userRoles.forEach( (role:any) => {
        const control = new FormControl<boolean>(false)
        this.userForm.addControl(role.identifier, control)
      })
    // }

    this.setupRolesSelect()
    this.loadAdditionalContexts(context)
  }

  private async loadAdditionalContexts(context: any): Promise<void> {
    this.isSuperContext = !!context?.superContext

    const currentRow = { id: context.identifier, name: context?.contextName ?? context.identifier, selected: true }
    this.contextRows = [currentRow]
    this.contextSelectionOrder = [currentRow.id]

    if (!this.isSuperContext) return

    // const subContextIds: string[] = (context.subContextIds ?? []).filter((id: string) => !!id && id !== context.identifier)
    const subContextIds: string[] = this.settingsService.getSubcontextIds()
    if (!subContextIds.length) return

    this.loadingAdditionalContexts = true
    const rows = await Promise.all(subContextIds.map(async id => {
      try {
        const snap = await getDoc(doc(this.firestore, NetworkConstants.COLLECTION_CONTEXT, id))
        const name = (snap.data() as any)?.name ?? id
        return { id, name, selected: false }
      } catch {
        return { id, name: id, selected: false }
      }
    }))
    this.contextRows = [currentRow, ...rows]
    this.loadingAdditionalContexts = false
  }

  /** Keeps `contextSelectionOrder` in sync with a row's checkbox — call from the template on toggle. */
  toggleContext(row: { id: string; name: string; selected: boolean }, selected: boolean): void {
    row.selected = selected
    this.contextSelectionOrder = this.contextSelectionOrder.filter(id => id !== row.id)
    if (selected) this.contextSelectionOrder.push(row.id)
  }

  get hasSelectedContext(): boolean {
    return this.contextSelectionOrder.length > 0
  }

  private computeRoleOptions(): { identifier: string; title: string }[] {
    const fixed = [
      { identifier: 'roleAnsteller',    title: this.translateService.instant('FORM.userForm.roleAnsteller') },
      { identifier: 'roleJagdleiter',   title: this.translateService.instant('FORM.userForm.roleJagdleiter') },
      { identifier: 'roleUser',         title: this.translateService.instant('FORM.userForm.roleUser') },
      { identifier: 'roleHunter',       title: this.translateService.instant('FORM.userForm.roleHunter') },
      { identifier: 'roleRevierLeiter', title: this.translateService.instant('FORM.userForm.roleRevierLeiter') },
      { identifier: 'roleRecorder',     title: this.translateService.instant('FORM.userForm.roleRecorder') },
    ]
    const custom = this.userRoles.map((r: any) => ({ identifier: r.identifier, title: r.title ?? r.identifier })) ?? []
    return [...fixed, ...custom]
  }

  private setupRolesSelect(): void {
    this.rolesSelectSub?.unsubscribe()

    this.roleOptions = this.computeRoleOptions()
    const initial = this.roleOptions.filter(r => this.userForm.get(r.identifier)?.value === true).map(r => r.identifier)
    this.rolesSelectCtrl.setValue(initial, { emitEvent: false })

    this.rolesSelectSub = this.rolesSelectCtrl.valueChanges.subscribe((selected: string[] | null) => {
      let ids = selected ?? []
      if (ids.includes('roleRecorder') && ids.length > 1) {
        ids = ids.filter(id => id !== 'roleRecorder')
        this.rolesSelectCtrl.setValue(ids, { emitEvent: false })
      }
      const idSet = new Set(ids)
      this.roleOptions.forEach(r => {
        const ctrl = this.userForm.get(r.identifier)
        if (ctrl) ctrl.setValue(idSet.has(r.identifier))
      })
    })
  }

  trackByIdentifier(_index: number, item: { identifier: string }): string {
    return item.identifier
  }

  determineRoles(roles: string[]) {
    return {
      roleAnsteller: roles.includes(UserRoles.ANSTELLER),
      roleJagdleiter: roles.includes(UserRoles.JAGDLEITER),
      roleUser: roles.includes(UserRoles.USER),
      roleHunter: roles.includes(UserRoles.HUNTER),
      roleRevierLeiter: roles.includes(UserRoles.REVIERLEITER),
      roleRecorder: roles.includes(UserRoles.RECORDER)
    }
  }

  async onSubmit() {
    if (!this.hasSelectedContext) return
    this.userForm.controls.email.enable()

    const {
      language,email,
      roleAnsteller,roleJagdleiter, roleUser,roleHunter,roleRevierLeiter, roleRecorder,
    } = this.userForm.value

    const roles = buildRoles({roleAnsteller, roleJagdleiter, roleUser, roleHunter, roleRevierLeiter, roleRecorder})
    let customUserRoles = this.userRoles.filter(r => this.userForm.get(r.identifier)?.value === true).map( r=> r.identifier)
    const allRoles = [...roles, ...customUserRoles]
    const createUser: IFirestoreInviteUser = { email, language, roles: allRoles }

    // The first context the user selected (checkbox click order) becomes the primary/Hauptkontext;
    // everything else selected afterward is sent as an additional context.
    const [mainContextId, ...additionalContextIds] = this.contextSelectionOrder
    const additionalContexts = additionalContextIds
      .map(id => ({ contextId: id, roles: allRoles }))

    try {
      this.userFeedbackService.loading.emit(true)

      let data: any = {
        contextId: mainContextId,//: string,
        userName: this.user$,//: string // Name of user who invites
        user: createUser,//: RegisterUser
      }
      if (additionalContexts.length) data.additionalContexts = additionalContexts


      const callable = this.functions.httpsCallable('admin-registerUser')
      const reply: any = await callable(data).toPromise().then(async value => {
        this.userFeedbackService.loading.emit(false)
        const sbSuccessSave = await this.translateService.get('changes_saved').toPromise()
        this.userFeedbackService.showSnackBar.emit(sbSuccessSave) 
        this.data.onSuccessfullySubmitted()
      })
      
    } catch (error) {
      console.error(error)
      this.userFeedbackService.loading.emit(false)
      this.userFeedbackService.showDialog.emit(
        {
          entryComponent: GenericDialogComponent,
          title: "Error",
          message: error.error.message ? error.error.message : JSON.stringify(error),
        }
      )
    }

  }

  onCancle = async () => {
    this.data.onCancle()
  }

}
